import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../lib/auth";
import api from "../lib/api";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";

export default function ChangePasswordPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/account";

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords don't match");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/change-password", null, {
        params: { current_password: currentPassword, new_password: newPassword },
      });
      const updatedUser = await refresh();
      const dest = updatedUser?.role && updatedUser.role !== "customer" ? "/admin" : redirectTo;
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="change-password-page" className="max-w-md mx-auto px-6 py-16">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-welljio-accent" strokeWidth={1.8} />
        <p className="text-xs uppercase tracking-widest text-welljio-subtle">Security step</p>
      </div>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-3">Set a new password.</h1>
      <p className="text-sm text-welljio-muted mb-8">
        {user?.name ? `Welcome, ${user.name}. ` : ""}
        For account security, you need to set your own password before continuing.
      </p>

      <form onSubmit={submit} className="bg-white border border-slate-100 rounded-3xl p-8 space-y-4" data-testid="change-password-form">
        <input
          data-testid="change-password-current"
          type="password"
          required
          placeholder="Current (temporary) password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className="w-full border border-slate-200 rounded-2xl px-5 py-3.5 outline-none focus:border-welljio-accent text-sm"
        />
        <input
          data-testid="change-password-new"
          type="password"
          required
          minLength={8}
          placeholder="New password (min. 8 characters)"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full border border-slate-200 rounded-2xl px-5 py-3.5 outline-none focus:border-welljio-accent text-sm"
        />
        <input
          data-testid="change-password-confirm"
          type="password"
          required
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full border border-slate-200 rounded-2xl px-5 py-3.5 outline-none focus:border-welljio-accent text-sm"
        />
        {error && <p data-testid="change-password-error" className="text-sm text-red-600">{error}</p>}
        <button
          data-testid="change-password-submit"
          disabled={loading}
          type="submit"
          className="w-full bg-welljio-ink text-white rounded-full py-4 font-medium hover:bg-slate-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Setting password...</> : <>Set password &amp; continue <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></>}
        </button>
      </form>
    </div>
  );
}
